import type {ReminderWeekday} from '../../domain/reminder-weekday.value-object';
import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {SettingsRepository} from '../../application/ports/settings.repository';
import {
  InvalidSettingsInputError,
  NetworkError,
  ServerError,
  UnauthorizedError,
  type SettingsError,
} from '../../domain/errors';
import type {UserProfile, WritableBodyMetrics} from '../../domain/user-profile';
import type {
  ReminderScheduleInterval,
  ReminderKind,
  UserReminder,
} from '../../domain/user-reminder';
import type {ProfileDtoToUserProfileMapper} from '../mappers/profile-dto-to-user-profile.mapper';
import type {ReminderDtoToUserReminderMapper} from '../mappers/reminder-dto-to-user-reminder.mapper';

export class RpcSettingsRepository implements SettingsRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly profileMapper: ProfileDtoToUserProfileMapper,
    private readonly reminderMapper: ReminderDtoToUserReminderMapper,
  ) {}

  async getProfile(): Promise<Result<UserProfile, SettingsError>> {
    try {
      const dto = await parseResponse(this.rpcClient.profile.$get());
      return ok(this.profileMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async updateBodyMetrics(
    metrics: WritableBodyMetrics,
  ): Promise<Result<UserProfile, SettingsError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.profile['body-metrics'].$patch({
          json: metrics,
        }),
      );
      return ok(this.profileMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async updateAvatar(file: File): Promise<Result<UserProfile, SettingsError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.profile.avatar.$put({
          form: {
            avatar: file,
          },
        }),
      );
      return ok(this.profileMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async deleteAvatar(): Promise<Result<UserProfile, SettingsError>> {
    try {
      const dto = await parseResponse(this.rpcClient.profile.avatar.$delete());
      return ok(this.profileMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async listReminders(): Promise<Result<readonly UserReminder[], SettingsError>> {
    try {
      const dto = await parseResponse(this.rpcClient.reminders.$get());
      return ok(dto.map(item => this.reminderMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async listDueReminders(): Promise<Result<readonly UserReminder[], SettingsError>> {
    try {
      const dto = await parseResponse(this.rpcClient.reminders.due.$get());
      return ok(dto.map(item => this.reminderMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async updateReminder(
    kind: ReminderKind,
    patch: {
      readonly interval?: ReminderScheduleInterval;
      readonly weekdays?: readonly ReminderWeekday[];
      readonly enabled?: boolean;
    },
  ): Promise<Result<UserReminder, SettingsError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.reminders[':kind'].$patch({
          param: {kind},
          json: {
            interval: patch.interval,
            enabled: patch.enabled,
            weekdays: patch.weekdays === undefined ? undefined : [...patch.weekdays],
          },
        }),
      );
      return ok(this.reminderMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async acknowledgeReminder(kind: ReminderKind): Promise<Result<UserReminder, SettingsError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.reminders[':kind'].acknowledge.$post({
          param: {kind},
        }),
      );
      return ok(this.reminderMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  private mapError(cause: unknown): Result<never, SettingsError> {
    if (cause instanceof DetailedError) {
      if (cause.statusCode === 401) {
        return err(new UnauthorizedError());
      }
      if (cause.statusCode === 400) {
        return err(new InvalidSettingsInputError());
      }
      return err(new ServerError());
    }
    return err(new NetworkError());
  }
}
